import {
    ChoiceCard,
    Colors,
    COMPONENT_RADIUS,
    FIXED_ICON_SIZES,
    Icon,
    Text,
    TooltipProvider,
    type CustomRadioOptionProps,
} from '@amelio/ui-library';

import type { IconProps } from '@amelio/ui-library';

import { FeedbackSettingPill } from '@/components/FeedbackSettingsPills/FeedbackSettingsPills';
import { TOOLTIP_DELAY_MS } from '@/constants/Tooltip';

interface TileGlyphProps {
    iconName: IconProps['name'];
    isChecked: boolean;
}

const TileGlyph = ({ iconName, isChecked }: TileGlyphProps) => (
    <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isChecked ? 'bg-primary-100' : 'bg-base-50'}`}
    >
        <Icon
            name={iconName}
            size={FIXED_ICON_SIZES.md}
            color={isChecked ? Colors.PRIMARY : Colors.BASE}
            ariaHidden={true}
        />
    </span>
);

/** What a choice means: one line of plain text, or a checked list when there is more than one thing to say. */
export type Feedback360SettingMeaning = string | string[];

const TileDetails = ({ meaning }: { meaning: Feedback360SettingMeaning }) =>
    typeof meaning === 'string' ? (
        <Text
            as={'span'}
            color={Colors.BASE_600}
            className={'text-xs'}
        >
            {meaning}
        </Text>
    ) : (
        // A label only holds phrasing content, so the list is spelled with roles.
        <span
            role={'list'}
            className={'flex flex-col gap-1.5'}
        >
            {meaning.map((consequence) => (
                <span
                    key={consequence}
                    role={'listitem'}
                    className={'flex items-start gap-2'}
                >
                    <Icon
                        name={'essential-checkmark'}
                        size={FIXED_ICON_SIZES.xsPlus}
                        color={Colors.PRIMARY}
                        ariaHidden={true}
                        className={'mt-0.5 shrink-0'}
                    />

                    <Text
                        as={'span'}
                        color={Colors.BASE_600}
                        className={'text-xs'}
                    >
                        {consequence}
                    </Text>
                </span>
            ))}
        </span>
    );

interface Feedback360SettingTileProps extends CustomRadioOptionProps {
    iconName: IconProps['name'];
    meaning: Feedback360SettingMeaning;
}

/** One choice of a 360 setting and what choosing it means, selectable over its whole surface. */
export const Feedback360SettingTile = ({
    iconName,
    meaning,
    ...optionProps
}: Feedback360SettingTileProps) => (
    <ChoiceCard
        {...optionProps}
        leading={
            <TileGlyph
                iconName={iconName}
                isChecked={optionProps.isChecked}
            />
        }
    >
        <TileDetails meaning={meaning} />
    </ChoiceCard>
);

interface Feedback360SettingFixedTileProps {
    label: string;
    iconName: IconProps['name'];
    meaning: Feedback360SettingMeaning;
    /** Why this value is the only one shown, on hover, focus or tap. */
    reason: string;
    dataTestId: string;
}

/** The value of a 360 setting decided elsewhere: the look of the chosen tile, without being a choice. */
export const Feedback360SettingFixedTile = ({
    label,
    iconName,
    meaning,
    reason,
    dataTestId,
}: Feedback360SettingFixedTileProps) => (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
        <FeedbackSettingPill
            explanation={[reason]}
            dataTestId={dataTestId}
            className={`flex cursor-default flex-col gap-3 ${COMPONENT_RADIUS['2xl']} border-primary shadow-choice-selected border bg-white p-4 sm:p-5`}
        >
            <span className={'flex items-center gap-3'}>
                <TileGlyph
                    iconName={iconName}
                    isChecked={true}
                />

                <Text
                    as={'span'}
                    size={'small'}
                    weight={'semibold'}
                >
                    {label}
                </Text>
            </span>

            <span>
                <TileDetails meaning={meaning} />
            </span>
        </FeedbackSettingPill>
    </TooltipProvider>
);
